// JobsManager.js - Main component (much smaller now)
import React, { useState, useEffect } from "react";
import { useAuth } from "../App";

// Custom hooks
import { useJobs } from "./hooks/useJobs";
import { useJobHistory } from "./hooks/useJobHistory";

// Components
import JobsList from "./components/JobsList";
import JobDetails from "./components/JobDetails";

// Modals
import CreateJobModal from "./components/modals/CreateJobModal";
import ErrorRecordsModal from "./components/modals/ErrorRecordsModal";
import ReuploadModal from "./components/modals/ReuploadModal";
import HistoryModal from "./components/modals/HistoryModal";
import HistoryErrorModal from "./components/modals/HistoryErrorModal";

const JobsManager = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Modal states
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [errorRecords, setErrorRecords] = useState([]);
  const [errorJobName, setErrorJobName] = useState("");

  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [reuploadJob, setReuploadJob] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyJob, setHistoryJob] = useState(null);

  const [showHistoryErrorModal, setShowHistoryErrorModal] = useState(false);
  const [historyErrorData, setHistoryErrorData] = useState([]);
  const [historyErrorTitle, setHistoryErrorTitle] = useState("");

  const { hasPermission } = useAuth();

  // Custom hooks
  const {
    jobs,
    loading,
    masterData,
    comparisonResults,
    loadJobs,
    viewJobDetails,
    deleteJob,
    exportJobResults,
    showErrorRecords,
    startAR04Reupload,
  } = useJobs({
    setErrorRecords,
    setErrorJobName,
    setShowErrorsModal,
    setReuploadJob,
    setShowReuploadModal,
  });

  const { jobHistory, showJobHistory, showHistoryErrorDetails } = useJobHistory(
    {
      setHistoryJob,
      setShowHistoryModal,
      setHistoryErrorData,
      setHistoryErrorTitle,
      setShowHistoryErrorModal,
    }
  );

  useEffect(() => {
    loadJobs();
  }, []);

  const handleJobSelect = (job) => {
    setSelectedJob(job);
    viewJobDetails(job);
  };

  const handleBackToList = () => {
    setSelectedJob(null);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>จัดการงาน</h1>
          <p>สร้างและจัดการงานเปรียบเทียบ AR04</p>
        </div>
        {hasPermission("canManageJobs") && (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ สร้างงานใหม่
          </button>
        )}
      </div>

      {/* Main Content */}
      {!selectedJob ? (
        <JobsList
          jobs={jobs}
          loading={loading}
          onViewDetails={handleJobSelect}
          onShowErrors={showErrorRecords}
          onShowHistory={showJobHistory}
          onStartReupload={startAR04Reupload}
          onExport={exportJobResults}
          onDelete={deleteJob}
          hasPermission={hasPermission}
        />
      ) : (
        <JobDetails
          job={selectedJob}
          masterData={masterData}
          comparisonResults={comparisonResults}
          onBack={handleBackToList}
          onExport={exportJobResults}
          hasPermission={hasPermission}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateJobModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadJobs();
          }}
        />
      )}

      {showErrorsModal && (
        <ErrorRecordsModal
          show={showErrorsModal}
          errorRecords={errorRecords}
          jobName={errorJobName}
          onClose={() => setShowErrorsModal(false)}
        />
      )}

      {showReuploadModal && reuploadJob && (
        <ReuploadModal
          show={showReuploadModal}
          job={reuploadJob}
          onClose={() => {
            setShowReuploadModal(false);
            setReuploadJob(null);
          }}
          onSuccess={() => {
            setShowReuploadModal(false);
            setReuploadJob(null);
            loadJobs();
          }}
        />
      )}

      {showHistoryModal && historyJob && (
        <HistoryModal
          show={showHistoryModal}
          job={historyJob}
          history={jobHistory}
          onClose={() => setShowHistoryModal(false)}
          onShowErrorDetails={showHistoryErrorDetails}
        />
      )}

      {showHistoryErrorModal && (
        <HistoryErrorModal
          show={showHistoryErrorModal}
          title={historyErrorTitle}
          errorData={historyErrorData}
          onClose={() => setShowHistoryErrorModal(false)}
        />
      )}
    </div>
  );
};

export default JobsManager;
